import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../../utils/api";
import BulkFileUpload from "../../Scope1/FugitiveEmmissions/BulkFileUpload";
import AddInvestment from "../../Masters/Scope3/Investment/AddInvestment";

const EXCEL_INVESTMENT_NAME_KEY = "Name of investment";
const EXCEL_INVESTMENT_TYPE_KEY = "Investment Type";

const BULK_UPLOAD_JSON_SNACK_DURATION = 4000;

const normalizeInvestmentNameKey = (value) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getEnteredInvestmentName = (row = {}) =>
  String(
    row[EXCEL_INVESTMENT_NAME_KEY] ??
      row["Name of investment."] ??
      row.investmentName ??
      row.investeeProjectName ??
      row["Investee/Project Name"] ??
      row.projectName ??
      row["Project Name"] ??
      ""
  ).trim();

const buildInvestmentMasterLookupMap = (masters = []) => {
  const map = new Map();
  masters.forEach((item) => {
    const investmentName = String(item?.investmentName || "").trim();
    if (investmentName) {
      map.set(normalizeInvestmentNameKey(investmentName), item);
    }
    const investeeProjectName = String(item?.investeeProjectName || "").trim();
    if (investeeProjectName) {
      const legacyKey = normalizeInvestmentNameKey(investeeProjectName);
      if (!map.has(legacyKey)) {
        map.set(legacyKey, item);
      }
    }
  });
  return map;
};

const resolveMasterFromSheetLabel = (label, lookupMap) => {
  const key = normalizeInvestmentNameKey(label);
  if (!key || !(lookupMap instanceof Map)) return null;
  return lookupMap.get(key) || null;
};

const getMatchedFlag = (row) => {
  if (!row) return false;
  if (typeof row.investmentMatched === "boolean") return row.investmentMatched;
  if (typeof row.investeeProjectNameMatched === "boolean")
    return row.investeeProjectNameMatched;
  if (typeof row.investmentTypeMatched === "boolean")
    return row.investmentTypeMatched;
  if (typeof row.matched === "boolean") return row.matched;
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  return matchedKey ? Boolean(row[matchedKey]) : false;
};

const setMatchedFlag = (row, next) => {
  if (!row) return row;
  if (typeof row.investmentMatched === "boolean")
    return { ...row, investmentMatched: next };
  if (typeof row.investeeProjectNameMatched === "boolean")
    return { ...row, investeeProjectNameMatched: next };
  if (typeof row.investmentTypeMatched === "boolean")
    return { ...row, investmentTypeMatched: next };
  if (typeof row.matched === "boolean") return { ...row, matched: next };
  const matchedKey = Object.keys(row).find((k) => /Matched$/i.test(k));
  if (matchedKey) return { ...row, [matchedKey]: next };
  return { ...row, matched: next };
};

const BulkUploadReview = ({
  matchData = [],
  setMatchData,
  bulkUploadTable,
  onClose,
  onConfirm,
  formDataBulkUpload = [],
  setFormDataBulkUpload,
  setsnackOpen,
  bulkUploadUrl,
}) => {
  const [masterInvestments, setMasterInvestments] = useState([]);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [addMasterOpen, setAddMasterOpen] = useState(false);
  const [addMasterElement, setAddMasterElement] = useState(null);
  const [pendingCreatedProjectName, setPendingCreatedProjectName] = useState("");
  const [addRowIndex, setAddRowIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const [finalJson, setFinalJson] = useState(formDataBulkUpload);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [hasValidationBeenAttempted, setHasValidationBeenAttempted] =
    useState(false);
  const [documentUploadConfirmed, setDocumentUploadConfirmed] = useState(false);
  const [documentUploadType, setDocumentUploadType] = useState("none");
  const [commonDocument, setCommonDocument] = useState(null);
  const [individualDocuments, setIndividualDocuments] = useState({});
  const [showNoDocumentDialog, setShowNoDocumentDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [skippedPreview, setSkippedPreview] = useState([]);

  const masterFormInputData = useMemo(
    () => [
      {
        fieldName: "investmentName",
        title: "Name of investment",
        placeholder: "Enter name",
        readOnly: true,
      },
      {
        fieldName: "alreadyIncluded",
        title: "Already included in Scope 1 and Scope 2?",
        type: "boolean",
      },
      { fieldName: "consolidationApproach", title: "Consolidation Approach" },
      {
        fieldName: "portfolioBasisStartYear",
        title: "Portfolio Basis (Start Date)",
        type: "portfolioYear",
        fixedDateText: "1st April",
      },
      {
        fieldName: "portfolioBasisEndYear",
        title: "Portfolio Basis (End Date)",
        type: "portfolioYear",
        fixedDateText: "31st March",
        optional: true,
      },
      {
        fieldName: "thresholdValue",
        title: "Threshold for excluding investments (%)",
        placeholder: "Enter Value",
      },
      {
        fieldName: "includeInScope3",
        title: "Include investee Scope 3 emissions?",
        type: "boolean",
      },
      { fieldName: "investmentType", title: "Investment Type" },
      {
        fieldName: "sector",
        title: "Sector(s) the investee company operates in",
      },
      {
        fieldName: "countryOfOperation",
        title: "Country of operation",
        placeholder: "Tap to select",
        type: "select",
      },
    ],
    []
  );

  useEffect(() => {
    if (Array.isArray(formDataBulkUpload)) setFinalJson(formDataBulkUpload);
  }, [formDataBulkUpload]);

  useEffect(() => {
    const fetchMasters = async () => {
      setLoadingMasters(true);
      try {
        const queryParams = new URLSearchParams({
          limit: "1000",
          page: "1",
        }).toString();
        const res = await get(`/master/investmentMasters?${queryParams}`);
        setMasterInvestments(
          (res?.data?.data || []).filter((item) => item?.alreadyIncluded === false)
        );
      } catch (e) {
        setMasterInvestments([]);
      } finally {
        setLoadingMasters(false);
      }
    };
    if (bulkUploadTable) fetchMasters();
  }, [bulkUploadTable]);

  const masterLookupMap = useMemo(
    () => buildInvestmentMasterLookupMap(masterInvestments),
    [masterInvestments]
  );

  const sortedMasterInvestments = useMemo(() => {
    return [...(masterInvestments || [])].sort((a, b) =>
      String(a?.investmentName || "").localeCompare(String(b?.investmentName || ""))
    );
  }, [masterInvestments]);

  const resolveMasterByLabel = (label) =>
    resolveMasterFromSheetLabel(label, masterLookupMap);

  const getSelectedInvestmentForIndex = (index) => {
    const row = formDataBulkUpload?.[index];
    const selected =
      row?.[EXCEL_INVESTMENT_NAME_KEY] || row?.investmentName || "";
    const val = String(selected || "").trim();
    if (!val || val === "add_new") return val;

    const master = resolveMasterByLabel(val);
    if (master?.investmentName) return master.investmentName;

    return sortedMasterInvestments.some(
      (item) =>
        normalizeInvestmentNameKey(item?.investmentName) ===
        normalizeInvestmentNameKey(val)
    )
      ? val
      : "";
  };

  const setSelectedInvestmentForIndex = (index, nextValue) => {
    const master = resolveMasterByLabel(nextValue);
    const canonicalName = String(master?.investmentName || nextValue || "").trim();
    const applySelection = (entry = {}) => ({
      ...entry,
      [EXCEL_INVESTMENT_NAME_KEY]: canonicalName,
      investmentName: canonicalName,
      [EXCEL_INVESTMENT_TYPE_KEY]:
        master?.investmentType || entry?.[EXCEL_INVESTMENT_TYPE_KEY] || "",
      investmentType: master?.investmentType || entry?.investmentType || "",
    });

    setFormDataBulkUpload((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      arr[index] = applySelection(existing);
      return arr;
    });
    setFinalJson((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      const existing = arr[index] || {};
      arr[index] = applySelection(existing);
      return arr;
    });
  };

  useEffect(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return;
    if (!masterLookupMap.size) return;

    let rowChanged = false;
    const nextMatchData = matchData.map((row, index) => {
      const alreadyMatched = getMatchedFlag(row);
      const enteredRaw = getEnteredInvestmentName(row);
      const master = resolveMasterByLabel(enteredRaw);

      if (!master) return row;

      const canonicalName = String(master.investmentName || "").trim();
      if (!canonicalName) return row;

      const rowData = formDataBulkUpload?.[index] || row;
      const currentLabel = getEnteredInvestmentName(rowData);
      if (
        normalizeInvestmentNameKey(currentLabel) !==
        normalizeInvestmentNameKey(canonicalName)
      ) {
        setSelectedInvestmentForIndex(index, canonicalName);
      }

      if (!alreadyMatched) {
        rowChanged = true;
        return setMatchedFlag(row, true);
      }
      return row;
    });

    if (rowChanged) setMatchData(nextMatchData);
  }, [matchData, masterLookupMap]);

  const disableConfirm = useMemo(() => {
    if (!Array.isArray(matchData) || matchData.length === 0) return true;
    return matchData.some((row, idx) => {
      const matched = getMatchedFlag(row);
      if (matched) return false;
      const selected = getSelectedInvestmentForIndex(idx);
      return !selected || selected === "add_new";
    });
  }, [matchData, formDataBulkUpload]);

  const openAddMasterDrawer = (index) => {
    const entered = String(getEnteredInvestmentName(matchData?.[index]) || "").trim();
    setAddRowIndex(index);
    setPendingCreatedProjectName(entered);
    setAddMasterElement({
      investmentName: entered,
    });
    setAddMasterOpen(true);
    setExpandedRows((prev) => ({ ...prev, [index]: true }));
  };

  const refreshMasterDataAndMarkMatched = async () => {
    try {
      const queryParams = new URLSearchParams({
        limit: "1000",
        page: "1",
      }).toString();
      const res = await get(`/master/investmentMasters?${queryParams}`);
      const list = (res?.data?.data || []).filter(
        (item) => item?.alreadyIncluded === false
      );
      setMasterInvestments(list);
      if (typeof addRowIndex === "number" && pendingCreatedProjectName) {
        const createdMaster = resolveMasterFromSheetLabel(
          pendingCreatedProjectName,
          buildInvestmentMasterLookupMap(list)
        );
        const canonicalName =
          createdMaster?.investmentName || pendingCreatedProjectName;
        setSelectedInvestmentForIndex(addRowIndex, canonicalName);
        setMatchData((prev) =>
          (prev || []).map((r, i) =>
            i === addRowIndex ? setMatchedFlag(r, true) : r
          )
        );
      }
    } finally {
      setExpandedRows((prev) => ({
        ...prev,
        ...(addRowIndex !== null ? { [addRowIndex]: false } : {}),
      }));
      setAddRowIndex(null);
      setPendingCreatedProjectName("");
      setAddMasterOpen(false);
      setAddMasterElement(null);
    }
  };

  const handleDocumentUploadConfirmation = (confirmed) => {
    setDocumentUploadConfirmed(confirmed);
    if (!confirmed) {
      setDocumentUploadType("none");
      setCommonDocument(null);
      setIndividualDocuments({});
      setFinalJson((prev) =>
        Array.isArray(prev)
          ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
          : prev
      );
    }
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleDocumentUploadTypeChange = (event) => {
    setDocumentUploadType(event.target.value);
    setCommonDocument(null);
    setIndividualDocuments({});
    setUploadValidationErrors([]);
    setHasValidationBeenAttempted(false);
  };

  const handleCommonDocumentChange = (files) => {
    setCommonDocument(files);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": files }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry) => ({ ...entry, "Document (optional)": files }))
      );
    }
  };

  const handleCommonDocumentClear = () => {
    setCommonDocument(null);
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry) => ({ ...entry, "Document (optional)": null }))
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry) => ({ ...entry, "Document (optional)": null }))
      );
    }
  };

  const handleIndividualDocumentChange = (index, files) => {
    setIndividualDocuments((prev) => ({ ...prev, [index]: files }));
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": files } : entry
          )
        : prev
    );
    if (Array.isArray(finalJson) && finalJson.length > 0) {
      setFinalJson(
        finalJson.map((entry, i) =>
          i === index ? { ...entry, "Document (optional)": files } : entry
        )
      );
    }
  };

  const handleIndividualDocumentClear = (index) => {
    setIndividualDocuments((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormDataBulkUpload((prev) =>
      Array.isArray(prev)
        ? prev.map((entry, i) =>
            i === index ? { ...entry, "Document (optional)": null } : entry
          )
        : prev
    );
  };

  const validateUploadData = () => {
    const validationErrors = [];
    if (documentUploadConfirmed && documentUploadType === "none") {
      validationErrors.push(
        "Please select a document upload type when document upload is confirmed."
      );
    }
    return validationErrors;
  };

  const performUpload = async () => {
    try {
      setUploading(true);
      setSkippedPreview([]);
      const dataToUse =
        Array.isArray(finalJson) && finalJson.length > 0
          ? finalJson
          : formDataBulkUpload;
      const uploadData = {
        excelData: dataToUse,
        documentUploadType,
        commonDocument,
        individualDocuments,
      };
      const res = await post(bulkUploadUrl, uploadData);
      const inserted = Number(res?.data?.inserted ?? 0);
      const skipped = Number(res?.data?.skipped ?? 0);

      if (inserted === 0) {
        setSkippedPreview(res?.data?.skippedEntries || []);
        setsnackOpen?.({
          open: true,
          message:
            skipped > 0
              ? `No data inserted. ${skipped} row${skipped > 1 ? "s were" : " was"} skipped. Please review and try again.`
              : res?.data?.message || "No data inserted. Please review and try again.",
          severity: "error",
          autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
        });
        return;
      }

      const responseData = res?.data ?? {};
      const skippedCount = Number(responseData?.skipped ?? skipped ?? 0);
      const apiMessage = responseData?.message;
      setsnackOpen?.({
        open: true,
        message:
          skippedCount > 0
            ? apiMessage ||
              `${inserted} entr${inserted === 1 ? "y" : "ies"} inserted. ${skippedCount} row${skippedCount === 1 ? "" : "s"} skipped.`
            : apiMessage || "Uploaded successfully",
        severity: skippedCount > 0 ? "warning" : "success",
        autoHideDuration: BULK_UPLOAD_JSON_SNACK_DURATION,
      });

      onConfirm?.(responseData);
      onClose?.();
    } catch (e) {
      const errMessage = e?.response?.data?.details
        ?.map((el) => el?.error || el?.reason)
        ?.join(", ");
      setsnackOpen?.({
        open: true,
        message:
          errMessage || e?.response?.data?.message || "Upload failed",
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    setHasValidationBeenAttempted(true);
    const validationErrors = validateUploadData();
    if (validationErrors.length > 0) {
      setUploadValidationErrors(validationErrors);
      return;
    }
    const needsDocs = documentUploadConfirmed && documentUploadType !== "none";
    if (needsDocs) {
      const hasCommonDoc =
        documentUploadType === "same" &&
        commonDocument &&
        commonDocument.length > 0;
      const hasIndividualDocs =
        documentUploadType === "individual" &&
        Object.keys(individualDocuments).length > 0 &&
        Object.values(individualDocuments).some((docs) => docs && docs.length > 0);
      if (!hasCommonDoc && !hasIndividualDocs) {
        setShowNoDocumentDialog(true);
        return;
      }
    }
    await performUpload();
  };

  if (!bulkUploadTable) return null;

  return (
    <>
      <Dialog
        open={bulkUploadTable}
        onClose={onClose}
        maxWidth="lg"
        sx={{
          "& .MuiDialog-paper": {
            width: "85%",
            margin: "16px",
            maxHeight: "calc(100% - 32px)",
            zIndex: 1000,
          },
        }}
      >
        <DialogTitle>Review Bulk Upload Data</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Review unmatched investment names and map them with master investments.
          </Typography>

          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Data Entered</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "30%" }}>Master Data</TableCell>
                  <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Action</TableCell>
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold", width: "20%" }}>Document</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>Name of investment</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Name of investment</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} />
                  {documentUploadConfirmed && documentUploadType === "individual" && (
                    <TableCell sx={{ fontWeight: "bold" }} />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {(matchData || []).map((row, index) => {
                  const entered = getEnteredInvestmentName(row);
                  const matched = getMatchedFlag(row);
                  return (
                    <React.Fragment key={index}>
                    <TableRow>
                      <TableCell>{entered || "-"}</TableCell>
                      <TableCell>
                        {matched ? (
                          <Chip label="Matched" color="success" size="small" />
                        ) : (
                          <FormControl size="small" sx={{ minWidth: "14rem" }}>
                            <Select
                              value={getSelectedInvestmentForIndex(index) || ""}
                              onChange={(e) => {
                                const nextValue = e.target.value;
                                if (nextValue === "add_new") {
                                  setSelectedInvestmentForIndex(index, "add_new");
                                  openAddMasterDrawer(index);
                                  return;
                                }
                                setSelectedInvestmentForIndex(index, nextValue);
                                setMatchData((prev) =>
                                  (prev || []).map((r, i) =>
                                    i === index ? setMatchedFlag(r, true) : r
                                  )
                                );
                                setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                setAddMasterOpen(false);
                              }}
                              fullWidth
                              displayEmpty
                              disabled={loadingMasters}
                            >
                              <MenuItem value="" disabled>
                                Tap to Select
                              </MenuItem>
                              <MenuItem value="add_new">Add New Investment +</MenuItem>
                              {sortedMasterInvestments.map((item) => {
                                const name = String(item?.investmentName || "").trim();
                                if (!name) return null;
                                return (
                                  <MenuItem key={item._id || name} value={name}>
                                    {name}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                      <TableCell>
                        {!matched &&
                          (expandedRows[index] ? (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                setAddMasterOpen(false);
                              }}
                              sx={{ minWidth: "80px" }}
                            >
                              Close
                            </Button>
                          ) : (
                            <Chip
                              label={getSelectedInvestmentForIndex(index) ? "Selected" : "Unmatched"}
                              color={getSelectedInvestmentForIndex(index) ? "info" : "warning"}
                              size="small"
                            />
                          ))}
                      </TableCell>
                      {documentUploadConfirmed && documentUploadType === "individual" && (
                        <TableCell>
                          <BulkFileUpload
                            handleChange={handleIndividualDocumentChange}
                            handleClear={handleIndividualDocumentClear}
                            filesList={
                              individualDocuments[index] ||
                              formDataBulkUpload?.[index]?.["Document (optional)"] ||
                              finalJson?.[index]?.["Document (optional)"] ||
                              []
                            }
                            index={index}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                        colSpan={documentUploadConfirmed && documentUploadType === "individual" ? 4 : 3}
                      >
                        <Collapse
                          in={Boolean(expandedRows[index]) && addRowIndex === index}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ mt: 2 }}>
                            <AddInvestment
                              id=""
                              addData={addMasterOpen && addRowIndex === index}
                              setAddData={(next) => {
                                setAddMasterOpen(next);
                                if (!next) {
                                  setExpandedRows((prev) => ({ ...prev, [index]: false }));
                                }
                              }}
                              data={masterInvestments}
                              element={addMasterElement}
                              addUrl="/master/investmentMasters"
                              updateUrl="/master/investmentMasters"
                              fetchData={refreshMasterDataAndMarkMatched}
                              setsnackOpen={setsnackOpen}
                              formInputData={masterFormInputData}
                              embedded
                              embeddedTitle="Add Investment"
                            />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Document Upload
            </Typography>
            <Stack spacing={2} sx={{ width: "100%" }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">Do you want to upload document?</FormLabel>
                <RadioGroup
                  value={documentUploadConfirmed ? "yes" : "no"}
                  onChange={(e) => handleDocumentUploadConfirmation(e.target.value === "yes")}
                  row
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </FormControl>
              {documentUploadConfirmed && (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend">Document Upload Type</FormLabel>
                  <RadioGroup value={documentUploadType} onChange={handleDocumentUploadTypeChange} row>
                    <FormControlLabel value="same" control={<Radio />} label="Same File for All Records" />
                    <FormControlLabel value="individual" control={<Radio />} label="Individual File for Each Record" />
                  </RadioGroup>
                </FormControl>
              )}
              {documentUploadConfirmed && documentUploadType === "same" && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Upload Common Document:
                </Typography>
                <BulkFileUpload
                  handleChange={handleCommonDocumentChange}
                  handleClear={handleCommonDocumentClear}
                  filesList={commonDocument}
                />
              </Box>
              )}
            </Stack>
          </Box>

          {hasValidationBeenAttempted && uploadValidationErrors.length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: "#ffebee", borderRadius: 1, border: "1px solid #f44336" }}>
              <Typography variant="h6" color="error" sx={{ mb: 1, fontWeight: "bold" }}>
                Error:
              </Typography>
              {uploadValidationErrors.map((error, index) => (
                <Typography key={index} variant="body2" color="error" sx={{ mb: 0.5 }}>
                  - {error}
                </Typography>
              ))}
            </Box>
          )}

          {skippedPreview.length > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                backgroundColor: "#fff8e1",
                borderRadius: 1,
                border: "1px solid #ffb300",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                Skipped rows
              </Typography>
              {skippedPreview.map((entry, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  •{" "}
                  {typeof entry === "string"
                    ? entry
                    : entry?.reason ||
                      entry?.error ||
                      entry?.message ||
                      JSON.stringify(entry)}
                </Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={onClose} variant="outlined" color="primary" sx={{ marginRight: "1rem" }} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmUpload} variant="contained" color="primary" disabled={disableConfirm || uploading}>
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showNoDocumentDialog} onClose={() => setShowNoDocumentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>No Documents Uploaded</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have selected to upload documents but haven't uploaded any files.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Do you want to proceed without uploading documents?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "1rem" }}>
          <Button onClick={() => setShowNoDocumentDialog(false)} variant="outlined" color="primary" sx={{ marginRight: "1rem" }}>
            Upload Documents
          </Button>
          <Button
            onClick={() => {
              setShowNoDocumentDialog(false);
              performUpload();
            }}
            variant="contained"
            color="primary"
          >
            Proceed Without Documents
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadReview;
